import { computed, DestroyRef, Injectable, signal } from '@angular/core';
import {
  fromEvent,
  merge,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throttleTime,
  timer,
} from 'rxjs';

export interface IdleDetectionConfig {
  idleTime: number;
  timeoutTime?: number;
  activityEvents?: string[];
  trackAcrossTabs?: boolean;
}

const DEFAULT_CONFIG: Required<IdleDetectionConfig> = {
  idleTime: 300,
  timeoutTime: 60,
  activityEvents: [
    'mousemove',
    'mousedown',
    'keydown',
    'scroll',
    'touchstart',
    'click',
  ],
  trackAcrossTabs: false,
};

@Injectable({
  providedIn: 'root',
})
export class IdleDetectionService {
  private _config = DEFAULT_CONFIG;
  private _activitySubject = new Subject<void>();
  private readonly STORAGE_KEY = 'last_activity_time';
  private readonly stopped$ = new Subject<void>();

  private readonly isIdleSignal = signal(false);
  private readonly isTimedOutSignal = signal(false);
  private readonly lastActivitySignal = signal(Date.now());
  private readonly secondsUntilIdleSignal = signal(0);

  readonly isIdle = this.isIdleSignal.asReadonly();
  readonly isTimedOut = this.isTimedOutSignal.asReadonly();
  readonly lastActivity = this.lastActivitySignal.asReadonly();
  readonly secondsUntilIdle = this.secondsUntilIdleSignal.asReadonly();

  readonly status = computed(() => {
    if (this.isTimedOut()) return 'timed-out';
    if (this.isIdle()) return 'idle';

    return 'active';
  });

  configure(config: Partial<IdleDetectionConfig> = {}) {
    this._config = { ...DEFAULT_CONFIG, ...config };
  }

  start() {
    this.reset();
    this.setupActivityListeners();
    this.startIdleTimer();

    if (this._config.trackAcrossTabs) {
      this.setupCrossTabSync();
    }
  }

  stop() {
    this._activitySubject.complete();
    this.stopped$.next();
    this.reset();
  }

  reset() {
    this.isIdleSignal.set(false);
    this.isTimedOutSignal.set(false);
    this.updateLastActivity();
  }

  registerCleanup(ref: DestroyRef) {
    ref.onDestroy(() => {
      this.stop();
    });
  }

  private setupActivityListeners() {
    const activityEvents = this._config.activityEvents.map((event) =>
      fromEvent(window, event, { passive: true })
    );

    const activity$ = merge(...activityEvents).pipe(
      takeUntil(this.stopped$),
      throttleTime(500),
      tap(() => this.onActivity())
    );

    activity$.subscribe();
  }

  private startIdleTimer() {
    this._activitySubject
      .pipe(
        startWith(undefined),
        switchMap(() => {
          this.reset();

          return timer(0, 1000);
        }),
        takeUntil(this.stopped$)
      )
      .subscribe({
        next: () => {
          const now = Date.now();
          const lastActivity = this.lastActivitySignal();
          const elapsedSeconds = Math.floor((now - lastActivity) / 1000);
          const idleSeconds = this._config.idleTime;
          const timeoutSeconds = this._config.timeoutTime;

          const remaining = Math.max(0, idleSeconds - elapsedSeconds);
          this.secondsUntilIdleSignal.set(remaining);

          if (!this.isIdleSignal() && elapsedSeconds >= idleSeconds) {
            this.isIdleSignal.set(true);
          }

          if (
            this.isIdleSignal() &&
            !this.isTimedOutSignal() &&
            elapsedSeconds >= idleSeconds + timeoutSeconds
          ) {
            this.isTimedOutSignal.set(true);
          }
        },
      });
  }

  private onActivity() {
    if (this.isIdleSignal() || this.isTimedOutSignal()) {
      this.reset();
    }

    this.updateLastActivity();
    this._activitySubject.next();
  }

  private updateLastActivity() {
    const now = Date.now();

    this.lastActivitySignal.set(now);

    if (this._config.trackAcrossTabs) {
      localStorage.setItem(this.STORAGE_KEY, now.toString());
    }
  }

  private setupCrossTabSync() {
    const storage$ = fromEvent<StorageEvent>(window, 'storage').pipe(
      tap((event) => {
        if (event.key === this.STORAGE_KEY && event.newValue) {
          const timestamp = parseInt(event.newValue, 10);

          if (timestamp > this.lastActivitySignal()) {
            this.lastActivitySignal.set(timestamp);
            this._activitySubject.next();
          }
        }
      })
    );

    storage$.subscribe();

    const storedValue = localStorage.getItem(this.STORAGE_KEY);

    if (storedValue) {
      const timestamp = parseInt(storedValue, 10);

      if (timestamp > this.lastActivitySignal()) {
        this.lastActivitySignal.set(timestamp);
      }
    }
  }
}
